import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadAuthUser } from '../services/auth';
import { toCanonicalPersonaId } from '../utils/persona';
import { useAppSettings } from '../components/providers/AppSettingsProvider';
import { getPageBackgroundGradient } from '../config/theme';
import type { CheckInListItem } from '../services/exploreService';
import { getDateKey } from '../services/journalService';
import { PRESET_ACTIVITY_EMOJI } from './CheckIn/data/events';

const isSameDay = (a: Date, b: Date) => getDateKey(a) === getDateKey(b);

const isYesterday = (date: Date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(date, yesterday);
};

const getMonthStart = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const normalizePersonaId = (id?: string | null): string =>
  toCanonicalPersonaId(id);

const PERSONA_CARD_COLOR_MAP: Record<string, string> = {
  cheerleader: '#FFA658',
  sleep_companion: '#0B5BDC',
  active_advisor: '#69FF58',
  listener: '#D91C91',
};

function getPersonaCardStyle(personaId?: string | null): React.CSSProperties {
  const key = normalizePersonaId(personaId);
  const base = PERSONA_CARD_COLOR_MAP[key];
  if (!base) return {};
  if (key === 'listener') {
    return {
      background: 'linear-gradient(180deg, rgba(217, 28, 145, 0.55) 0%, rgba(155, 60, 230, 0.5) 100%)',
    };
  }
  if (key === 'active_advisor') {
    return {
      background: 'linear-gradient(180deg, rgba(0, 154, 10, 0.5) 0%, rgba(105, 255, 88, 0.5) 100%)',
    };
  }
  if (key === 'sleep_companion') {
    return {
      background: 'linear-gradient(180deg, rgba(55, 132, 235, 0.5) 0%, rgba(109, 188, 252, 0.5) 100%)',
    };
  }
  if (key === 'cheerleader') {
    return {
      background: 'linear-gradient(180deg, rgba(232, 85, 74, 0.5) 0%, rgba(245, 160, 122, 0.5) 100%)',
    };
  }
  return { backgroundColor: base };
}

const formatTime = (ms: number): string => {
  if (!ms) return '';
  const date = new Date(ms);
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${hours}:${minutes}${ampm}`;
};

function activityCornerEmoji(item: CheckInListItem): string | null {
  if (item.activity_emoji?.trim()) return item.activity_emoji.trim();
  const tag = item.activity_tag?.trim();
  if (tag && PRESET_ACTIVITY_EMOJI[tag]) return PRESET_ACTIVITY_EMOJI[tag];
  return null;
}

const Journals: React.FC = () => {
  const { t, language, theme } = useAppSettings();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const location = useLocation();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [currentMonth, setCurrentMonth] = useState<Date>(() => getMonthStart(today));
  const [checkInItems, setCheckInItems] = useState<CheckInListItem[]>([]);
  const [streakStats, setStreakStats] = useState({ totalDays: 0, longestStreak: 0, currentStreak: 0 });
  const [calendarDataMap, setCalendarDataMap] = useState<Record<string, any>>({});

  // --- Fetch check-in list + backend data ---
  const refreshBackendData = useCallback(async () => {
    const { fetchStreak, fetchCalendar, fetchCheckInList, getUserTimezone } = await import('../services/exploreService');
    const {
      loadCachedCheckInList,
      mergeCheckInApiWithCache,
      saveCachedCheckInList,
    } = await import('../services/checkInListCache');
    const tz = getUserTimezone();
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    const cached = loadCachedCheckInList(year, month);
    setCheckInItems(cached);

    const [streakData, days, apiItems] = await Promise.all([
      fetchStreak(tz),
      fetchCalendar({ year, month }, tz),
      fetchCheckInList(year, month, tz),
    ]);

    if (streakData) {
      setStreakStats({
        totalDays: streakData.total_checkin_days,
        longestStreak: streakData.longest_streak,
        currentStreak: streakData.current_streak,
      });
    }

    const map: Record<string, any> = {};
    for (const day of days) map[day.date] = day;
    setCalendarDataMap(map);

    const merged = mergeCheckInApiWithCache(apiItems, cached);
    setCheckInItems(merged);
    saveCachedCheckInList(year, month, merged);
  }, [currentMonth]);

  useEffect(() => {
    refreshBackendData();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refreshBackendData();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshBackendData]);

  /** 签到完成回到日记时立刻拉最新列表（避免偶发缓存/时序） */
  useEffect(() => {
    const st = location.state as { fromCheckIn?: boolean } | null;
    if (location.pathname !== '/journals' || !st?.fromCheckIn) return;
    void refreshBackendData();
    navigate('/journals', { replace: true, state: {} });
  }, [location.pathname, location.state, navigate, refreshBackendData]);

  // --- Group check-in items by local_date ---
  const itemsByDate = useMemo(() => {
    const map: Record<string, CheckInListItem[]> = {};
    for (const item of checkInItems) {
      const key = item.local_date;
      if (!map[key]) map[key] = [];
      map[key].push(item);
    }
    return map;
  }, [checkInItems]);

  // --- Sorted dates with check-ins (descending) ---
  const datesWithCheckIns = useMemo(() => {
    return Object.keys(itemsByDate)
      .sort((a, b) => b.localeCompare(a))
      .map((key) => new Date(key + 'T00:00:00'));
  }, [itemsByDate]);

  // --- Calendar ---
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startPadding = firstDay.getDay() - 1;
    if (startPadding < 0) startPadding = 6;
    const days: (Date | null)[] = [];
    for (let i = 0; i < startPadding; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
    return days;
  }, [currentMonth]);

  const calendarRows = useMemo(() => {
    const rows: (Date | null)[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      const row = calendarDays.slice(i, i + 7);
      while (row.length < 7) row.push(null);
      rows.push(row);
    }
    return rows;
  }, [calendarDays]);

  const goToPreviousMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const goToNextMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  const goToToday = () => setCurrentMonth(getMonthStart(today));

  const formatMonthTitle = (date: Date): string => {
    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    return `${month} ${date.getFullYear()}`;
  };

  const getEmojiForDay = useCallback((date: Date): string => {
    const dateKey = getDateKey(date);
    return calendarDataMap[dateKey]?.emoji || '';
  }, [calendarDataMap]);

  const formatDateLabel = (date: Date): string => {
    if (isSameDay(date, today)) return t('journals.today');
    if (isYesterday(date)) return t('journals.yesterday');
    return date.toLocaleDateString(language, { month: 'short', day: 'numeric' });
  };

  const isFutureDate = (date: Date): boolean => {
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);
    return dateOnly.getTime() > today.getTime();
  };

  // --- 计算当前连续打卡的日期集合 ---
  const streakDates = useMemo(() => {
    const set = new Set<string>();
    if (streakStats.currentStreak <= 1) return set;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const todayKey = getDateKey(now);

    // 今天有打卡记录则连续区间包含今天，否则从昨天往前数
    const todayHasData = Boolean(calendarDataMap[todayKey]) || Boolean(itemsByDate[todayKey]);
    const streakEnd = new Date(now);
    if (!todayHasData) streakEnd.setDate(streakEnd.getDate() - 1);

    for (let i = 0; i < streakStats.currentStreak; i++) {
      const d = new Date(streakEnd);
      d.setDate(d.getDate() - i);
      set.add(getDateKey(d));
    }
    return set;
  }, [streakStats.currentStreak, calendarDataMap, itemsByDate]);

  // --- Navigate to detail page ---
  const emotionDisplayLabel = useCallback(
    (emotionTag: string) => t(`checkIn.tags.${emotionTag}`) || emotionTag,
    [t],
  );

  const handleCardClick = (item: CheckInListItem, dateLabel: string) => {
    const isConversation = item.type === 'conversation_auto';
    const hasNote = Boolean(item.journal_text?.trim());
    const hasTitle = Boolean(item.journal_title?.trim());
    const isManual = item.type === 'manual_entry';
    navigate('/journal-detail', {
      state: {
        dateKey: item.local_date,
        dateLabel,
        conversations: [],
        emotions: [],
        conversationId: item.source_conversation_id || undefined,
        personaId: item.persona_id || undefined,
        existingSummary:
          isManual && (hasTitle || hasNote)
            ? {
                title: item.journal_title || emotionDisplayLabel(item.emotion_tag),
                primaryEmotion: {
                  emoji: item.emoji,
                  label: emotionDisplayLabel(item.emotion_tag),
                },
                lumiHeard: item.journal_text || '',
                detectedEmotions: item.detected_emotions || [],
              }
            : isConversation && item.journal_title
              ? {
                  title: item.journal_title,
                  primaryEmotion: { emoji: item.emoji, label: item.emotion_tag },
                  lumiHeard: item.journal_text || '',
                  detectedEmotions: item.detected_emotions || [],
                }
              : undefined,
      },
    });
  };

  // --- Render a single check-in card ---
  const renderCard = (item: CheckInListItem, dateLabel: string) => {
    const hasText = Boolean(item.journal_text?.trim());
    const hasTitle = Boolean(item.journal_title?.trim());
    const isLargeCard = hasText && (item.journal_text?.length || 0) > 30;
    const isConversation = item.type === 'conversation_auto';
    const isManual = item.type === 'manual_entry';
    const personaCardStyle = isConversation ? getPersonaCardStyle(item.persona_id) : {};
    const cornerEmoji = activityCornerEmoji(item);

    const clickable =
      (isConversation && Boolean(item.source_conversation_id)) ||
      (isManual && (hasText || hasTitle));

    const manualCompact = isManual && !hasText && !hasTitle;

    if (manualCompact) {
      return (
        <div
          key={item.id}
          className={`rounded-3xl overflow-hidden transition-transform ${
            isDark
              ? 'bg-gradient-to-br from-white/10 via-white/5 to-white/[0.02]'
              : 'bg-gradient-to-br from-white/60 via-white/40 to-white/30'
          }`}
        >
          <div className="flex">
            <div className="w-1 flex-shrink-0 rounded-l-3xl" style={{ backgroundColor: item.color }} />
            <div className="flex flex-1 items-stretch gap-3 p-4 min-w-0">
              <div className="flex shrink-0 items-center justify-center text-[2.75rem] leading-none" aria-hidden>
                {item.emoji || '✨'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className={`text-lg font-semibold leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {emotionDisplayLabel(item.emotion_tag)}
                    </h3>
                    <span className={`mt-1 block text-xs ${isDark ? 'text-white/45' : 'text-gray-400'}`}>
                      {formatTime(item.created_at)}
                    </span>
                  </div>
                  {cornerEmoji && (
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl leading-none ${
                        isDark ? 'bg-white/15 text-white' : 'bg-white/70 text-gray-800'
                      }`}
                      aria-hidden
                    >
                      {cornerEmoji}
                    </div>
                  )}
                </div>
                {item.activity_tag && !cornerEmoji && (
                  <p className={`mt-2 truncate text-xs font-medium ${isDark ? 'text-white/55' : 'text-gray-500'}`}>
                    {item.activity_tag}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={item.id}
        onClick={clickable ? () => handleCardClick(item, dateLabel) : undefined}
        className={`rounded-3xl overflow-hidden ${clickable ? 'cursor-pointer active:scale-[0.98]' : ''} transition-transform ${
          isDark
            ? 'bg-gradient-to-br from-white/10 via-white/5 to-white/[0.02]'
            : 'bg-gradient-to-br from-white/60 via-white/40 to-white/30'
        }`}
        style={personaCardStyle}
      >
        <div className="flex">
          {/* Color bar */}
          <div className="w-1 flex-shrink-0 rounded-l-3xl" style={{ backgroundColor: item.color }} />

          <div className="flex-1 p-4 min-w-0">
            {/* Title row: title + time + activity */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h3 className={`text-base font-semibold leading-snug truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {item.journal_title || (isManual ? emotionDisplayLabel(item.emotion_tag) : item.emotion_tag)}
                </h3>
                <span className={`text-xs mt-0.5 block ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {formatTime(item.created_at)}
                </span>
              </div>
              {item.activity_tag && (
                <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'}`}>
                  {cornerEmoji && <span>{cornerEmoji}</span>}
                  {item.activity_tag}
                </span>
              )}
            </div>

            {/* Emotion badge */}
            <div className="mt-2">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${isDark ? 'bg-white/15 text-white' : 'bg-white/80 text-gray-800'}`}>
                {item.emoji && <span className="text-sm">{item.emoji}</span>}
                {isManual ? emotionDisplayLabel(item.emotion_tag) : item.emotion_tag}
              </span>
            </div>

            {/* Body text */}
            {hasText && (
              <p className={`mt-2 text-sm leading-relaxed ${isLargeCard ? 'line-clamp-2' : ''} ${isDark ? 'text-white/85' : 'text-gray-700'}`}>
                {item.journal_text}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 w-full h-screen overflow-hidden">
      <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />

      <div className="relative z-10 h-full flex flex-col overflow-y-auto">
        {/* Page title */}
        <div className="w-full px-6 pb-3 flex-shrink-0" style={{ paddingTop: '10vh' }}>
          <h1 className="text-2xl font-bold text-white">{t('journals.streakCalendar')}</h1>
        </div>

        {/* Stats */}
        <div className="w-full px-4 pb-3 flex-shrink-0">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: t('journals.stats.totalDays'), value: streakStats.totalDays },
              { label: t('journals.stats.longestStreak'), value: streakStats.longestStreak },
              { label: t('journals.stats.currentStreak'), value: streakStats.currentStreak },
            ].map(({ label, value }) => (
              <div
                key={label}
                className="rounded-2xl px-3 py-4 flex flex-col items-center justify-center"
                style={{
                  backgroundColor: 'rgba(63, 120, 255, 0.40)',
                  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.18)',
                }}
              >
                <span className="text-3xl font-bold text-white leading-none mb-1">{value}</span>
                <span className="text-[10px] font-semibold text-white/60 text-center leading-tight tracking-wide uppercase">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Calendar */}
        <div className="w-full px-4 pb-3 flex-shrink-0">
          <div
            className="rounded-3xl px-3 pb-4 pt-3"
            style={{
              backgroundColor: 'rgba(63, 120, 255, 0.40)',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.18)',
            }}
          >
            <div className="flex items-center justify-between mb-4 px-2">
              <button onClick={goToPreviousMonth} className="p-2 rounded-lg transition-colors hover:bg-white/20 text-white focus:outline-none" aria-label="Previous month">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button onClick={goToToday} className="text-xl font-semibold text-white tracking-wide hover:opacity-80 transition-opacity">
                {formatMonthTitle(currentMonth)}
              </button>
              <button onClick={goToNextMonth} className="p-2 rounded-lg transition-colors hover:bg-white/20 text-white focus:outline-none" aria-label="Next month">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="w-full h-px bg-white/20 mb-3" />

            <div className="grid grid-cols-7 mb-1">
              {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d) => (
                <div key={d} className="text-center text-[10px] font-semibold text-white/50 tracking-wider py-1">{d}</div>
              ))}
            </div>

            <div className="space-y-1">
              {calendarRows.map((row, rowIndex) => {
                return (
                  <div key={rowIndex} className="grid grid-cols-7">
                    {row.map((day, dayIndex) => {
                      if (!day) return <div key={dayIndex} className="flex items-center justify-center h-10" />;
                      const isToday = isSameDay(day, today);
                      const isFuture = isFutureDate(day);
                      const emoji = isFuture ? '' : getEmojiForDay(day);
                      const dayKey = getDateKey(day);
                      const isStreak = streakDates.has(dayKey);

                      // 判断相邻格子是否也在连续打卡区间内，用于拼接圆角
                      const prevDay = dayIndex > 0 ? row[dayIndex - 1] : null;
                      const nextDay = dayIndex < 6 ? row[dayIndex + 1] : null;
                      const prevIsStreak = prevDay ? streakDates.has(getDateKey(prevDay)) : false;
                      const nextIsStreak = nextDay ? streakDates.has(getDateKey(nextDay)) : false;

                      let streakRoundClass = '';
                      if (isStreak) {
                        if (!prevIsStreak && !nextIsStreak) streakRoundClass = 'rounded-full';
                        else if (!prevIsStreak) streakRoundClass = 'rounded-l-full';
                        else if (!nextIsStreak) streakRoundClass = 'rounded-r-full';
                      }

                      return (
                        <div
                          key={dayIndex}
                          className={`flex items-center justify-center h-10 ${streakRoundClass}`}
                          style={isStreak ? { backgroundColor: 'rgba(255, 245, 47, 0.20)' } : undefined}
                        >
                          <div className="relative w-10 h-10 flex items-center justify-center">
                            {isToday && <div className="absolute inset-0 m-auto w-9 h-9 rounded-full bg-yellow-300" />}
                            {emoji ? (
                              <span className={`relative z-10 text-2xl leading-none select-none ${isFuture ? 'opacity-30' : ''}`}>{emoji}</span>
                            ) : (
                              <div className={`relative z-10 w-8 h-8 rounded-full border transition-all ${isToday ? 'border-transparent' : isFuture ? 'border-white/15' : 'border-white/30'}`} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Check-in card list */}
        <div className="flex-1 px-6 pb-40 pt-2">
          {datesWithCheckIns.length === 0 ? (
            <div className={`backdrop-blur-md rounded-2xl p-8 border ${isDark ? 'bg-white/10 border-white/20' : 'bg-white/50 border-white/60'}`}>
              <div className="flex flex-col items-center justify-center">
                <p className={`text-lg font-medium mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  Oops! Nothing recorded here.
                </p>
                <div className="mb-6 flex justify-center">
                  <img src="/images/ai_character/no_record.png" alt="No records" className="max-w-full h-auto" style={{ maxHeight: '300px' }} />
                </div>
                <button
                  onClick={() => {
                    const user = loadAuthUser();
                    if (!user) {
                      toast.error(t('auth.loginRequired'));
                      navigate('/splash', { replace: true, state: { forceLogin: true } });
                    } else {
                      navigate('/whisper');
                    }
                  }}
                  className={`w-full font-medium py-3 px-4 rounded-xl transition-all border ${isDark
                    ? 'bg-white/20 hover:bg-white/30 text-white border-white/30'
                    : 'bg-white/60 hover:bg-white/80 text-gray-800 border-white/70'
                  }`}
                >
                  Start your conversation
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {datesWithCheckIns.map((date) => {
                const dateKey = getDateKey(date);
                const items = itemsByDate[dateKey] || [];
                const dateLabel = formatDateLabel(date);

                return (
                  <div key={dateKey} className="space-y-4">
                    <div className="flex items-center justify-center mb-1">
                      <span className={`text-xs font-medium uppercase tracking-wider opacity-60 ${isDark ? 'text-white' : 'text-gray-600'}`}>
                        {dateLabel}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {items.map((item) => renderCard(item, dateLabel))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Journals;
